import styled from '@emotion/styled';
import { yupResolver } from '@hookform/resolvers/yup';
import { List, Grid, Stack, Card } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useForm, FormProvider, FieldValues } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { ISubmissionSettingsTemplate, getCandidateSubmissionSettingsTemplate } from 'wisestep-api';
import {
  WsColors,
  ScrollableTabs,
  WsTypography,
  WsTypoColors,
  WsTypoVariants,
  WsButton,
  WsEditor,
  ControlledTextInput,
  Item,
  useWsEditor,
  TagsInput,
} from 'wisestep-base';
import { object, string } from 'yup';

import { SettingPageBreadCrumbs } from '../../../components/SettingPageBreadCrumbs/SettingPageBreadCrumbs';
import SettingsIndex from '../../../components/SettingsIndex';
import { IBreadCrumbsList } from '../../../components/WsBreadCrumbs';
import { useWorkspaceContext } from '../../../context/WorkspaceContext/WorkspaceContext';
import ErrorLogger from '../../../providers/ErrorLogger';
import { warning } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import CandidatePlaceholder from '../../emailSettings/components/templates/CandidatePlaceholder';
import JobPlaceholder from '../../emailSettings/components/templates/JobPlaceholder';
import RecruiterPlaceholder from '../../emailSettings/components/templates/RecruiterPlaceholder';

const StyledList = styled(List)({
  marginLeft: 15,
  maxHeight: '70vh',
  overflow: 'auto',
  '&& .Mui-selected, && .Mui-selected:hover': {
    backgroundColor: 'red',
    '&, & .MuiListItemIcon-root': {
      color: 'pink',
    },
  },
  '& .MuiListItem-root:hover': {
    backgroundColor: WsColors.TheBlues50,
    '&, & .MuiListItemIcon-root': {
      color: WsColors.TheBlues500,
    },
  },
});

const EmailTextBorder = styled(Item)(({ theme }) => ({
  borderBottom: '1px solid ' + WsColors.TertiaryShades100,
  borderRadius: 0,
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(1.5),
}));
const backUrlsList = (): IBreadCrumbsList[] => [
  {
    label: 'Settings',
    url: '/app-settings/crm',
  },
  {
    label: 'Customize Submission Flow',
    url: '/crm-settings/configure-submission-flow',
  },
  {
    label: 'Add Template',
    url: `${window.location.pathname}${window.location.search}`,
  },
];

export interface ILocationState {
  payload?: ISubmissionSettingsTemplate;
}

const SubmissionEmailTemplate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: workspaceId, userId } = useWorkspaceContext();
  const queryParams = new URLSearchParams(location.search);
  const templateName = queryParams.get('templateName') ?? '';
  const state = (location.state as ILocationState) ?? null;
  const payload = state?.payload || null;
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [templateData, setTemplateData] = useState<ISubmissionSettingsTemplate | null>(null);
  const [tabPanelPage, setTabPanelPage] = useState(0);
  const [currentEditor, setCurrentEditor] = useState<string>('subject');

  const TemplateDefaultValues = {
    template_name: templateData?.templateName ?? '',
    cc: templateData?.cc ?? [],
    bcc: templateData?.bcc ?? [],
    subject: templateData?.subject ?? '',
    body: templateData?.body ?? '',
  };

  useEffect(() => {
    const fetchTemplateData = async () => {
      if (payload) {
        setTemplateData(payload);
        return;
      }
      if (!isEmpty(templateName) && !payload) {
        try {
          const res = await getCandidateSubmissionSettingsTemplate(templateName, workspaceId ?? '');
          setTemplateData(res?.data || null);
        } catch (error) {
          setTemplateData(null);
        }
      }
    };

    fetchTemplateData();
  }, [templateName, payload, workspaceId]);

  const validationSchema = object().shape({
    template_name: string().required('The template name cannot be empty'),
    body: string().required('The body cannot be empty'),
  });

  const formOptions = {
    resolver: yupResolver(validationSchema),
    defaultValues: TemplateDefaultValues,
  };
  const methods = useForm(formOptions);
  const { handleSubmit, setValue, reset, watch } = methods;

  useEffect(() => {
    if (templateData) {
      reset({
        template_name: templateData.templateName ?? '',
        cc: templateData.cc ?? [],
        bcc: templateData.bcc ?? [],
        subject: templateData.subject ?? '',
        body: templateData.body ?? '',
        tags: templateData?.tags ?? '',
      });
    }
  }, [templateData, reset]);

  const onFormDiscard = () => {
    editor?.commands.setContent('');
    setValue('subject', ``);
    reset();
    navigate('/crm-settings/configure-submission-flow');
  };

  const onFormSubmitSave = async (data: FieldValues) => {
    const formData: ISubmissionSettingsTemplate = {
      templateName: data.template_name,
      workspaceId: workspaceId ?? '',
      createdBy: userId,
      bcc: data.bcc ?? [],
      cc: data.cc ?? [],
      subject: data.subject,
      body: data.body,
      fields: templateData?.fields,
      tags: data.tags,
    };
    if (!data.bcc || !data.cc || !data.subject) {
      setNotification(warning(`Oops , Please check CC, BCC and Subject are mandotory filed`));
    } else {
      navigate('/crm-settings/configure-submission-form', {
        state: {
          payload: formData,
        },
      });
    }
  };

  const formLabel = (label: string) => (
    <Grid xs={2} item>
      <Stack direction={'row'} justifyContent={'space-between'} pr={2}>
        <WsTypography color={WsTypoColors.SENARY} variant={WsTypoVariants.BODY1}>
          {label}
        </WsTypography>
        <WsTypography color={WsTypoColors.SENARY} variant={WsTypoVariants.BODY1}>
          :
        </WsTypography>
      </Stack>
    </Grid>
  );
  const EmailInputField = styled(TagsInput)(() => ({
    '.MuiOutlinedInput-root': {
      fieldset: {
        border: 'none',
      },
      padding: 0,
      margin: 0,
    },
    ':inputProps': {
      padding: 0,
    },
  }));
  const CCBCCInput = (name: 'cc' | 'bcc') => (
    <Grid xs={7} item>
      <EmailInputField
        name={name}
        onTagsChange={
          name === 'cc'
            ? (emails: string[]) => setValue('cc', emails)
            : (emails: string[]) => setValue('bcc', emails)
        }
        initialValue={
          name === 'cc' ? TemplateDefaultValues.cc ?? [] : TemplateDefaultValues.bcc ?? []
        }
        multiple={true}
      />
    </Grid>
  );

  const editor = useWsEditor(
    templateData?.body ?? '',

    (htmlBody) => {
      setValue('body', htmlBody);
    },
    () => {},
    () => setCurrentEditor('body')
  );
  useEffect(() => {
    if (editor && editor.commands) {
      editor.commands.setContent(templateData?.body ?? '');
    }
  }, [templateData?.body, editor]);

  const placeholderAction = (placeholder: string) => {
    if (currentEditor === 'subject') {
      const subjectValue = watch('subject');
      setValue('subject', `${subjectValue} ${placeholder}`);
    } else {
      editor?.commands.insertContent(placeholder);
    }
  };
  const [showBcc, setShowBcc] = useState<boolean>(false);

  const scrollList = [
    {
      tabName: 'Candidate',
    },
    {
      tabName: 'Job',
    },
    {
      tabName: 'Recruiter',
    },
  ];

  return (
    <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
      <FormProvider {...methods}>
        <form>
          <SettingPageBreadCrumbs breadCrumbsList={backUrlsList()}>
            <Grid container columnSpacing={3}>
              <Grid item xs={2}>
                <SettingsIndex
                  index={''}
                  options={[
                    {
                      label: '01. Email Template',
                      url: '/crm-settings/configure-submission-email-template',
                    },
                    {
                      label: '02. Submission Form',
                      url: '/crm-settings/configure-submission-form',
                    },
                  ]}
                  isNewSubmissionForm={true}
                />
              </Grid>
              <Grid item container xs={10}>
                <Card sx={{ minHeight: '100vh', width: '100%', p: 2 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={4}>
                      <ControlledTextInput
                        name="template_name"
                        labelText="Template Name *"
                        id="template_name"
                        placeholder="Enter Template Name"
                        variant="outlined"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <ControlledTextInput
                        name="tags"
                        labelText="Tags"
                        id="tags"
                        placeholder="Enter Tag"
                        variant="outlined"
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                  <Stack spacing={2} pt={2}>
                    <WsTypography bold={true} variant={WsTypoVariants.SUB_TITLE}>
                      Email Template
                    </WsTypography>
                    <WsTypography variant={WsTypoVariants.SUB_TITLE}>Placeholders</WsTypography>
                  </Stack>
                  <Grid container spacing={2} mt={0.5}>
                    <Grid item xs={4}>
                      <ScrollableTabs
                        scrollList={scrollList}
                        getTabIndex={(index: number) => setTabPanelPage(index)}
                        defaultValue={tabPanelPage}
                      ></ScrollableTabs>
                      <StyledList>
                        {tabPanelPage === 0 && (
                          <CandidatePlaceholder
                            onClickPlaceholder={placeholderAction}
                            isSubmissionEmail={true}
                          />
                        )}
                        {tabPanelPage === 1 && (
                          <JobPlaceholder onClickPlaceholder={placeholderAction} />
                        )}
                        {tabPanelPage === 2 && (
                          <RecruiterPlaceholder onClickPlaceholder={placeholderAction} />
                        )}
                      </StyledList>
                    </Grid>
                    <Grid bgcolor={WsColors.White} item xs={8}>
                      <>
                        <EmailTextBorder>
                          <Stack direction="row" alignItems={'center'}>
                            {formLabel('Subject')}
                            <Grid xs={10}>
                              <ControlledTextInput
                                name="subject"
                                type="text"
                                fullWidth
                                variant="standard"
                                onFocus={() => setCurrentEditor('subject')}
                                InputProps={{
                                  disableUnderline: true,
                                }}
                              />
                            </Grid>
                          </Stack>
                        </EmailTextBorder>

                        <Grid
                          container
                          direction="row"
                          justifyContent="space-between"
                          display="flex"
                        >
                          <Grid item xs={8}>
                            <EmailTextBorder>
                              <Stack direction="row" alignItems={'center'}>
                                {formLabel('CC')}
                                {CCBCCInput('cc')}
                              </Stack>
                            </EmailTextBorder>
                          </Grid>
                          <Grid
                            item
                            xs={2}
                            pt={2}
                            direction="row-reverse"
                            justifyContent="flex-end"
                          >
                            <WsButton onClick={() => setShowBcc(!showBcc)} variant="text">
                              {showBcc ? 'Hide BCC' : 'Show BCC'}
                            </WsButton>
                          </Grid>
                        </Grid>

                        {showBcc && (
                          <EmailTextBorder>
                            <Stack direction="row" alignItems={'center'}>
                              {formLabel('BCC')}
                              {CCBCCInput('bcc')}
                            </Stack>
                          </EmailTextBorder>
                        )}
                      </>
                      <WsEditor key="body" editor={editor} resizable={true} />
                    </Grid>
                    <Stack
                      direction="row"
                      spacing={1}
                      justifyContent="flex-end"
                      pt={2}
                      pr={2}
                      sx={{ width: '100%' }}
                    >
                      <WsButton onClick={onFormDiscard} variant="outlined" size="large">
                        Cancel
                      </WsButton>
                      <WsButton
                        onClick={handleSubmit(onFormSubmitSave)}
                        variant="contained"
                        size="large"
                      >
                        Continue
                      </WsButton>
                    </Stack>
                  </Grid>
                </Card>
              </Grid>
            </Grid>
          </SettingPageBreadCrumbs>
        </form>
      </FormProvider>
    </ErrorBoundary>
  );
};
export default SubmissionEmailTemplate;
